<template>
  <div ref="formRootEl">
    <form type="form" class="formkit-form">
      <ProductListLayout
        :show-filter="true"
        :results-text="''"
        :data-testid-productlist-total="21"
      >
        <template #filter>
          <ProductFilterSkeleton />
        </template>
        <template #actions>
          <div class="actions__container"></div>
        </template>
        <template #filterTags>
          <TagList :tags="[]" :is-small="true" />
        </template>
        <template #list>
          <ProductList
            :items="loadingList"
            size="three-quarters"
            :display-mode="props.view"
            :default-columns="defaultColumns"
            :attribute-columns="productsList.attributeColumns"
          >
            <template #card-footer>
              <LoadingSkeleton type="button" />
            </template>
          </ProductList>
          <ProductListPagination class="product-list-pagination" :items="[]" />
        </template>
      </ProductListLayout>
    </form>
  </div>
</template>

<script setup lang="ts">
import {
  LoadingSkeleton,
  ProductList,
  ProductListLayout,
  ProductListPagination,
  TagList
} from '@hypercodestudio/basler-components';
import type { ProductCardProps } from '@hypercodestudio/basler-components/dist/components/modules/ProductCard.vue';
import type { AttributeColumn } from '@hypercodestudio/basler-components/dist/components/modules/ProductListTable.vue';
import ProductFilterSkeleton from '~/components/shop/skeletons/ProductFilterSkeleton.vue';

interface Props {
  type: 'Specifications' | 'Discontinued';
  view: 'grid' | 'table-list';
}

const props = defineProps<Props>();

const { $textDictionary } = useNuxtApp();

const loadingList = computed((): ProductCardProps[] => {
  const product = {
    product: {
      id: '',
      name: 'Lorem ipsum',
      variantName: 'Lorem ipsum',
      description: 'Lorem ipsum',
      // XXX: wird nirgendwo angezeigt?
      price: -1,
      priceText: '',
      pricePrefix: '',
      attributes: [
        {
          key: 'lorem_ipsum',
          label: 'lorem_ipsum',
          value: 'lorem_ipsum'
        },
        {
          key: 'lorem_ipsum',
          label: 'lorem_ipsum',
          value: 'lorem_ipsum'
        },
        {
          key: 'lorem_ipsum',
          label: 'lorem_ipsum',
          value: 'lorem_ipsum'
        },
        {
          key: 'lorem_ipsum',
          label: 'lorem_ipsum',
          value: 'lorem_ipsum'
        },
        {
          key: 'lorem_ipsum',
          label: 'lorem_ipsum',
          value: 'lorem_ipsum'
        }
      ],
      media: {}
    },
    isLoading: true,
    attributesLoadingLines: 5,
    loadingLines: 5
  };
  const skeletonProducts = [];
  for (let i = 0; i < 21; i++) {
    skeletonProducts.push(product);
  }
  return skeletonProducts;
});

const productsList = computed<{
  list: ProductCardProps[];
  attributeColumns: AttributeColumn[];
}>(() => {
  return {
    list: [],
    attributeColumns: []
  };
});

const defaultColumns = computed(() => {
  const columns = [
    {
      key: 'media',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.media'] ?? '',
      hideColumnLabel: true
    },
    {
      key: 'title',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.title'] ?? ''
    }
  ];

  if (props.type === 'Specifications') {
    columns.push({
      key: 'priceText',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.priceText'] ?? ''
    });
  }

  const hasDescription = productsList.value.list.some(
    (card) => card.product.description === ''
  );
  if (!hasDescription) {
    columns.push({
      key: 'description',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.description'] ??
        '',
      hideColumnLabel: true
    });
  }

  return columns;
});
</script>

<style module>
.skeleton-template {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 32px;
  justify-items: center;
}

.skeleton-container {
  width: 293px;
  max-width: 293px;
  border-radius: 16px;
  box-shadow: 0 0 30px #00000040;
  overflow: hidden;
}

.skeleton-card {
  display: flex;
  flex-direction: column;

  --bslr-color-card-shadow: rgb(0 0 0 / 15%);
  background-color: var(--bslr-color-lucent-background);
  box-shadow: 0 0 20px var(--bslr-color-card-shadow);
}

.skeleton-card-content-container {
  display: flex;
  flex-direction: column;
  height: 450px;
  justify-content: space-between;
}

.skeleton-card-header {
  display: flex;
  gap: 10px;
  flex-direction: column;
  padding: 20px;
  justify-content: flex-end;
  height: 100%;
  background-color: #fff;
}

.skeleton-card-content {
  display: grid;
  grid-gap: 10px;
  padding: 20px;
  grid-template-columns: 1fr 1fr;
  justify-items: center;
}

.skeleton-card-content-item {
  width: 100% !important;
  height: 20px !important;
}

.skeleton-card-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px;
}

.skeleton-button-style {
  border: 0 none;
  border-radius: 40px !important;
  padding: 8px 20px;
  height: 35px !important;
  width: 100% !important;
}

@media (max-width: 1280px) {
  .skeleton-container {
    width: 284px;
    max-width: 284px;
  }
}

@media (max-width: 1024px) {
  .skeleton-template {
    grid-template-columns: 1fr 1fr;
  }

  .skeleton-container {
    width: 343px;
    max-width: 343px;
  }
}

@media (max-width: 768px) {
  .skeleton-container {
    width: 320px;
    max-width: 320px;
  }
}

@media (max-width: 700px) {
  .skeleton-template {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 400px) {
  .skeleton-container {
    width: 311px;
    max-width: 311px;
  }
}

@media (max-width: 300px) {
  .skeleton-container {
    width: 250px;
    max-width: 250px;
  }
}
</style>
